import { JSONContent } from "@tiptap/react";

import { articlesService } from "../../../apiServices/articles.service";
import { generateSummary } from "./article.helpers";

export type ArticleEditLang = "es" | "en" | "pt";
export const ARTICLE_LANGS: ArticleEditLang[] = ["es", "en", "pt"];
export const TRANSLATABLE_LANGS = ["en", "pt"] as const;
export type TranslatableLang = (typeof TRANSLATABLE_LANGS)[number];

export const EMPTY_CONTENT: JSONContent = {
  type: "doc",
  content: [{ type: "paragraph" }],
};

export const LANG_LABELS: Record<ArticleEditLang, string> = {
  es: "Español",
  en: "Inglés",
  pt: "Portugués",
};

export type ArticleLangContent = {
  title: string;
  resumen: string;
  content: JSONContent;
};

export const emptyLangContent = (): ArticleLangContent => ({
  title: "",
  resumen: "",
  content: EMPTY_CONTENT,
});

export const parseTranslatedContent = (contenido: unknown): JSONContent => {
  if (!contenido) return EMPTY_CONTENT;
  if (typeof contenido === "object") return contenido as JSONContent;
  if (typeof contenido === "string") {
    try {
      return JSON.parse(contenido);
    } catch {
      return EMPTY_CONTENT;
    }
  }
  return EMPTY_CONTENT;
};

export const langIsReadyToSave = (entry: ArticleLangContent): boolean =>
  entry.title.trim().length >= 2;

export const findTranslation = (list: any[] | undefined, lang: ArticleEditLang) =>
  list?.find((t: any) => String(t.idioma).toLowerCase() === lang);

export const langContentFromApi = (raw: any | undefined): ArticleLangContent => {
  if (!raw) return emptyLangContent();
  return {
    title: raw.titulo ?? "",
    resumen: raw.resumen ?? "",
    content: parseTranslatedContent(raw.contenido),
  };
};

export const spanishFingerprint = (entry: ArticleLangContent): string =>
  JSON.stringify({
    title: entry.title,
    resumen: entry.resumen,
    content: entry.content,
  });

export const toTranslationPayload = (
  lang: ArticleEditLang,
  entry: ArticleLangContent,
) => {
  const resumen = entry.resumen.trim() || generateSummary(entry.content);
  return {
    idioma: lang.toUpperCase(),
    titulo: entry.title.trim(),
    resumen: resumen.length >= 2 ? resumen.slice(0, 500) : `${resumen || "—"} `,
    contenido: entry.content,
  };
};

export const translateFromSpanish = async (
  spanish: ArticleLangContent,
  targetLang: TranslatableLang,
  categoryName: string,
): Promise<ArticleLangContent> => {
  const response = await articlesService.translate({
    lenguajeDestino: targetLang,
    lenguajeOrigen: "es",
    titulo: spanish.title,
    categoria: categoryName || "Tecnología",
    resumen: spanish.resumen?.trim() ? spanish.resumen : " ",
    contenido: spanish.content,
  });

  if (!response) {
    throw new Error("La traducción no devolvió contenido");
  }

  return {
    title: response.titulo ?? "",
    resumen: (response.resumen ?? "").trim(),
    content: parseTranslatedContent(response.contenido),
  };
};
